import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { getToken, setToken, clearToken } from '@/util/tokenManager';
import { useRouter } from 'next/router';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';
import i18n from '@/util/i18n';

interface AuthContextType {
  logged: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [logged, setLogged] = useState<boolean>(false);
  const router = useRouter();
  const showToast = useToast();
  const { t } = useTranslation();

  // 登录函数
  const login = () => {
    setLogged(true);
  };

  // 登出函数
  const logout = () => {
    setLogged(false);
    clearToken();
    router.push('/');
  };

  useEffect(() => {
    // 监听登出事件
    const handleAuthLogout = () => {
      setLogged(false);
      clearToken();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('auth-logout', handleAuthLogout);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('auth-logout', handleAuthLogout);
      }
    };
  }, []);

  useEffect(() => {
    const handleLogin = async () => {
      // 检查 URL 参数中是否有 t 和 lan
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('t');

      // 如果有语言参数，先切换语言
      // 注意：虽然 _app.js 也会切换语言，但这里需要 await 确保 toast 显示前语言已切换完成
      // if (lanParam && i18n.language !== lanParam) {
      //   await i18n.changeLanguage(lanParam);
      // }

      if (tokenFromUrl) {

        setToken(tokenFromUrl);
        login();
        const currentPath = window.location.pathname;
        if (currentPath === '/' || currentPath === '/playground_mobile') {
          router.push('/playground_mobile');
        }
      } else {
        // 如果 URL 中没有 t 参数，检查localStorage
        const accessToken = getToken();
        if (accessToken) {
          setLogged(true);
        }
      }
    };

    handleLogin();
  }, []);

  useEffect(() => {
    if (!router.isReady) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('t')) return;

    urlParams.delete('t');
    const currentPath = window.location.pathname;
    const isRootOrNoPath = !currentPath || currentPath === '/';
    const isCocomePremium = window.location.hostname === 'cocomepremium.com';

    if (isCocomePremium) {
      const search = urlParams.toString();
      const newUrl = `${currentPath}${search ? `?${search}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', newUrl);
      return;
    }

    const restQuery: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      restQuery[key] = value;
    });
    const targetPath = isRootOrNoPath ? '/playground_mobile' : currentPath;
    router.replace({ pathname: targetPath, query: restQuery }, undefined, { shallow: true });
  }, [router.isReady, router]);

  return <AuthContext.Provider value={{ logged, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
